/**
 * Minimal AWS Signature Version 4 signer (zero dependencies, Node `crypto`).
 *
 * Used by `cofactor status` to call the IAM-authed status write Function URL
 * and to call STS AssumeRole — without pulling the full AWS SDK into the
 * published CLI bundle. Correctness is locked against AWS's published
 * `get-vanilla` test vector in `status-sign.test.ts`.
 *
 * @module status-sign
 */
import crypto from 'node:crypto';

export interface Sv4Credentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
}

export interface SignInput {
  method: string;
  host: string;
  /** Absolute path, e.g. `/api/incidents`. */
  path: string;
  /** Canonical query string (already sorted/encoded), or ''. */
  query?: string;
  /** Extra headers to sign (host + x-amz-date are added automatically). */
  headers?: Record<string, string>;
  /** Raw request body. */
  payload?: string;
  service: string;
  region: string;
  credentials: Sv4Credentials;
  /** Basic ISO8601 stamp `YYYYMMDDTHHMMSSZ`. Pass for determinism in tests. */
  amzDate: string;
}

export interface SignResult {
  /** Final `Authorization` header value. */
  authorization: string;
  amzDate: string;
  signedHeaders: string;
  /** Hex SHA256 of the canonical request (asserted in tests). */
  canonicalRequestHash: string;
  /** Header map to send with the request (includes Authorization). */
  outgoingHeaders: Record<string, string>;
}

const sha256hex = (data: string): string =>
  crypto.createHash('sha256').update(data, 'utf8').digest('hex');

const hmac = (key: crypto.BinaryLike | crypto.KeyObject, data: string): Buffer =>
  crypto.createHmac('sha256', key).update(data, 'utf8').digest();

/** Current time as a SigV4 basic-format stamp. */
export function amzDateNow(now = new Date()): string {
  return now.toISOString().replace(/[:-]|\.\d{3}/g, '');
}

export function signRequest(input: SignInput): SignResult {
  const { method, host, path, service, region, credentials, amzDate } = input;
  const query = input.query ?? '';
  const payload = input.payload ?? '';
  const dateStamp = amzDate.slice(0, 8);
  const payloadHash = sha256hex(payload);

  // Assemble the headers that participate in signing.
  const signedHeaderMap: Record<string, string> = {
    host,
    'x-amz-date': amzDate,
    ...input.headers,
  };
  if (credentials.sessionToken) {
    signedHeaderMap['x-amz-security-token'] = credentials.sessionToken;
  }

  const lowerEntries = Object.entries(signedHeaderMap)
    .map(([k, v]) => [k.toLowerCase(), String(v).trim()] as const)
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));

  const canonicalHeaders = lowerEntries.map(([k, v]) => `${k}:${v}\n`).join('');
  const signedHeaders = lowerEntries.map(([k]) => k).join(';');

  const canonicalRequest = [
    method.toUpperCase(),
    path,
    query,
    canonicalHeaders,
    signedHeaders,
    payloadHash,
  ].join('\n');
  const canonicalRequestHash = sha256hex(canonicalRequest);

  const scope = `${dateStamp}/${region}/${service}/aws4_request`;
  const stringToSign = [
    'AWS4-HMAC-SHA256',
    amzDate,
    scope,
    canonicalRequestHash,
  ].join('\n');

  const kDate = hmac(`AWS4${credentials.secretAccessKey}`, dateStamp);
  const kRegion = hmac(kDate, region);
  const kService = hmac(kRegion, service);
  const kSigning = hmac(kService, 'aws4_request');
  const signature = crypto.createHmac('sha256', kSigning).update(stringToSign, 'utf8').digest('hex');

  const authorization =
    `AWS4-HMAC-SHA256 Credential=${credentials.accessKeyId}/${scope}, ` +
    `SignedHeaders=${signedHeaders}, Signature=${signature}`;

  const outgoingHeaders: Record<string, string> = {
    ...signedHeaderMap,
    'x-amz-content-sha256': payloadHash,
    Authorization: authorization,
  };

  return { authorization, amzDate, signedHeaders, canonicalRequestHash, outgoingHeaders };
}
